import { Component, OnInit, Input } from '@angular/core';
import { PopupDialog } from '@app/Common/popup/popup.dialog';
import { MatDialog } from '@angular/material/dialog';
import { LinkedFieldSet } from '@app/Models/linkedFieldSet';
import { SimpleFormFlowField } from '@app/Models/SimpleFormFlowField';
import { LinkedFieldsService } from '@app/Services/linkedFields.service';
import { TenantService } from '@app/Services/tenant.service';
import { FormFlowService } from '@app/Services/formFlow.service';
import { ExistingLinkedFieldSetViewModel } from '@app/Models/existingLinkedFieldSetViewModel';
import { FormFlowFieldTypes } from '@app/Constants/FormFlowFieldTypes';
import { MatCard, MatCardTitle, MatCardContent } from '@angular/material/card';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';

@Component({
    selector: 'my-modules-linked-fields',
    templateUrl: './module.linked-fields.component.html',
    styleUrls: ['./module.linked-fields.component.less'],
    imports: [MatCard, MatCardTitle, MatCardContent, MatIconButton, MatIcon, MatFormField, MatLabel, MatSelect, MatOption, MatButton]
})
export class ModuleLinkedFieldsComponent implements OnInit {
    constructor(
        public dialog: MatDialog,
        private readonly linkedFieldsService: LinkedFieldsService,
        private readonly tenantService: TenantService,
        private readonly formFlowService: FormFlowService) { }

    @Input() moduleId: string;
    @Input() version: number;
    addNewFieldToggle = false;
    existingLinkedFieldSets: ExistingLinkedFieldSetViewModel[] = [];
    availableParentFields: SimpleFormFlowField[];
    availableChildFields: SimpleFormFlowField[];
    selectedNewParentField = new SimpleFormFlowField();
    selectedNewChildField = new SimpleFormFlowField();
    showSaveErrorMessage = false;
    isSaving = false;

    ngOnInit(): void {
        this.fetchExistingLinkedFieldSets();
    }

    onAddNewButtonClick(): void {
        this.addNewFieldToggle = true;

        this.formFlowService.getFieldsOfType(this.tenantService.selectedTenant.TenantId, this.moduleId, this.version, FormFlowFieldTypes.List)
            .subscribe(result => {
                this.availableParentFields = result;
            });

        this.linkedFieldsService.getFieldsWithNoParentLinkedField(this.tenantService.selectedTenant.TenantId, this.moduleId, this.version, FormFlowFieldTypes.List)
            .subscribe(result => {
                this.availableChildFields = result;
            });
    }

    onParentFieldChange(field: any): void {
        this.selectedNewParentField.ParentPropertyName = field.value.ParentPropertyName;
        this.selectedNewParentField.StepId = field.value.StepId;
        this.selectedNewParentField.DisplayName = field.value.DisplayName;
        this.selectedNewParentField.ParentMultiPropertyName = field.value.ParentMultiPropertyName;

        this.getChildrenForParent(this.selectedNewParentField);

        if (this.selectedNewChildField !== null && (this.selectedNewChildField.StepId !== this.selectedNewParentField.StepId || this.selectedNewChildField.ParentPropertyName === this.selectedNewParentField.ParentPropertyName)) {
            this.selectedNewChildField = new SimpleFormFlowField();
        }
    }

    onChildFieldChange(field: any): void {
        this.selectedNewChildField.ParentPropertyName = field.value.ParentPropertyName;
        this.selectedNewChildField.StepId = field.value.StepId;
        this.selectedNewChildField.DisplayName = field.value.DisplayName;
        this.selectedNewChildField.ParentMultiPropertyName = field.value.ParentMultiPropertyName;
    }

    getChildrenForParent(parentField: SimpleFormFlowField): void {
        this.linkedFieldsService.getFieldsAtSameLevelLinkedField(parentField, this.tenantService.selectedTenant.TenantId, this.moduleId, this.version, FormFlowFieldTypes.List)
            .subscribe(result => {
                this.availableChildFields = result;
            });
    }

    onSaveNewFieldSetButtonClick(): void {
        if (this.isSaving) { return; }

        this.isSaving = true;
        this.showSaveErrorMessage = false;

        const linkedFieldSet: LinkedFieldSet = {
            LinkedFieldSetId: null,
            ParentStepId: this.selectedNewParentField.StepId,
            ParentPropertyName: this.selectedNewParentField.ParentPropertyName,
            ChildStepId: this.selectedNewChildField.StepId,
            ChildPropertyName: this.selectedNewChildField.ParentPropertyName
        };

        this.linkedFieldsService.add(this.tenantService.selectedTenant.TenantId, this.moduleId, this.version, linkedFieldSet)
            .subscribe(
                (res: LinkedFieldSet) => {
                    const lfs = new ExistingLinkedFieldSetViewModel();
                    lfs.LinkedFieldSetId = res.LinkedFieldSetId;
                    lfs.ParentDisplayName = this.selectedNewParentField.DisplayName;
                    lfs.ChildDisplayName = this.selectedNewChildField.DisplayName;

                    this.addNewFieldToggle = false;
                    this.existingLinkedFieldSets.push(lfs);
                    this.selectedNewParentField = new SimpleFormFlowField();
                    this.selectedNewChildField = new SimpleFormFlowField();
                    this.isSaving = false;
                },
                () => {
                    this.showSaveErrorMessage = true;
                    this.isSaving = false;
                }
            );
    }

    onCancelNewFieldSetButtonClick(): void {
        this.addNewFieldToggle = false;

        this.availableParentFields = new Array<SimpleFormFlowField>();
        this.availableChildFields = new Array<SimpleFormFlowField>();
        this.selectedNewParentField = new SimpleFormFlowField();
        this.selectedNewChildField = new SimpleFormFlowField();
    }

    onDeleteExistingFieldSetButtonClick(linkedFieldSetId: string): void {
        const dialogRef = this.dialog.open(PopupDialog);
        dialogRef.componentInstance.DialogTitle = 'Delete Field';
        dialogRef.componentInstance.DialogDescription = 'Are you sure?';
        dialogRef.componentInstance.DialogConfirmButtonText = 'Yes';
        dialogRef.componentInstance.DialogCancelButtonText = 'No';

        dialogRef.afterClosed()
            .subscribe(res => {
                if (res.Result) {
                    this.linkedFieldsService.remove(this.tenantService.selectedTenant.TenantId, this.moduleId, this.version, linkedFieldSetId)
                        .subscribe(() => {
                            this.existingLinkedFieldSets = this.existingLinkedFieldSets.filter(item =>
                                item.LinkedFieldSetId !== linkedFieldSetId);
                        });
                }
            });
    }

    fetchExistingLinkedFieldSets(): void {
        this.linkedFieldsService.get(this.tenantService.selectedTenant.TenantId, this.moduleId, this.version)
            .subscribe(result =>
                result.forEach(list => this.existingLinkedFieldSets.push(list))
            );
    }

    shouldShowChildFieldInDropDownList(childField: SimpleFormFlowField): boolean {
        return this.selectedNewParentField != null &&
            childField.ParentPropertyName !== this.selectedNewParentField.ParentPropertyName;
    }
}
